import { DebugLog } from './DebugLog';
import { isTauriRuntime } from './Environment';

type RuntimeState = {
  startedAt: string;
  lastHeartbeatAt: string;
  cleanExit: boolean;
  pendingSceneInit?: {
    scene: string;
    startedAt: string;
    data?: unknown;
  };
};

export type CrashReport = {
  startedAt: string;
  lastHeartbeatAt: string;
  recentLogs: string[];
  pendingSceneInit?: {
    scene: string;
    startedAt: string;
    data?: unknown;
  };
};

const STATE_KEY = 'jrpg-launcher.runtime-state.v1';
const REPORT_KEY = 'jrpg-launcher.crash-report.v1';

function shouldTrackCrashReports() {
  return isTauriRuntime() || import.meta.env.DEV;
}

function readState(): RuntimeState | undefined {
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    return raw ? (JSON.parse(raw) as RuntimeState) : undefined;
  } catch {
    return undefined;
  }
}

function writeState(state: RuntimeState) {
  try {
    window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {}
}

function writeCrashReport(report: CrashReport) {
  try {
    window.localStorage.setItem(REPORT_KEY, JSON.stringify(report));
  } catch {}
}

export function markAppStart() {
  if (typeof window === 'undefined') return;
  if (!shouldTrackCrashReports()) {
    try {
      window.localStorage.removeItem(STATE_KEY);
      window.localStorage.removeItem(REPORT_KEY);
    } catch {}
    return;
  }

  const previous = readState();
  if (previous && !previous.cleanExit) {
    writeCrashReport({
      startedAt: previous.startedAt,
      lastHeartbeatAt: previous.lastHeartbeatAt,
      pendingSceneInit: previous.pendingSceneInit,
      recentLogs: DebugLog.recent(20).map((entry) => {
        const hhmmss = entry.timestamp.slice(11, 19);
        return `${hhmmss} [${entry.scope}] ${entry.message}`;
      }),
    });
  }

  const now = new Date().toISOString();
  writeState({
    startedAt: now,
    lastHeartbeatAt: now,
    cleanExit: false,
  });

  window.setInterval(() => {
    const current = readState();
    if (!current) return;
    writeState({
      ...current,
      lastHeartbeatAt: new Date().toISOString(),
      cleanExit: false,
    });
  }, 1000);
}

export function markAppExit() {
  if (typeof window === 'undefined') return;
  if (!shouldTrackCrashReports()) return;
  const current = readState();
  if (!current) return;
  writeState({
    ...current,
    lastHeartbeatAt: new Date().toISOString(),
    cleanExit: true,
  });
}

export function markSceneInitStart(scene: string, data?: unknown) {
  if (typeof window === 'undefined') return;
  if (!shouldTrackCrashReports()) return;
  const current = readState();
  if (!current) return;

  writeState({
    ...current,
    lastHeartbeatAt: new Date().toISOString(),
    cleanExit: false,
    pendingSceneInit: {
      scene,
      startedAt: new Date().toISOString(),
      data,
    },
  });
}

export function markSceneInitComplete(scene: string) {
  if (typeof window === 'undefined') return;
  if (!shouldTrackCrashReports()) return;
  const current = readState();
  if (!current) return;

  if (current.pendingSceneInit?.scene !== scene) return;

  const nextState: RuntimeState = {
    startedAt: current.startedAt,
    lastHeartbeatAt: new Date().toISOString(),
    cleanExit: current.cleanExit,
  };
  writeState(nextState);
}

export function getPendingCrashReport(): CrashReport | undefined {
  if (typeof window === 'undefined') return undefined;
  if (!shouldTrackCrashReports()) return undefined;
  try {
    const raw = window.localStorage.getItem(REPORT_KEY);
    return raw ? (JSON.parse(raw) as CrashReport) : undefined;
  } catch {
    return undefined;
  }
}

export function clearPendingCrashReport() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REPORT_KEY);
  } catch {}
}
